import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import { Box, Divider, Paper, Typography, useTheme } from "@mui/material";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { type FC, type Ref, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { PanelHeader } from "../../../components/UIToolkit/molecules/PanelHeader";
import { MOTION } from "../../../theme/tokens";
import type { PbjCategoryScore } from "../../../utils/models";
import { ActionItemRow } from "./ActionItemRow";
import { cardShadow, PBJ_RADIUS } from "./colors";
import { computeCumulativePass } from "./cumulativeScoring.utils";
import { WEEKEND_REC_IDS } from "./planOutcome.utils";
import type { PeriodMode } from "./RecommendationsSidebar";
import type { PlanItem } from "./recommendations.constants";

// Offset (px) a freshly-added action is scrolled to below the viewport top, so it isn't flush with the edge.
const SCROLL_MARGIN_TOP_PX = 16;

type Props = {
	selectedPlan: PlanItem[];
	// HPPD categories (with band ladders + rawValue baselines) the cumulative
	// scoring pass scores each plan item's marginal points against.
	categories: PbjCategoryScore[];
	// Total points per raw measure point (`planPointsScale`) — per-item points in total-point units.
	pointsScale: number;
	onRemove: (id: PlanItem["id"]) => void;
	// Census + per-day-type quarter counts let us convert each item's per-PPD
	// spend rate into its real projected dollar cost. Weekday items multiply by
	// `quarterDays`; weekend items multiply by `quarterWeekendDays` because the
	// hours only land on weekend shifts (the spend rate is per-weekend-PPD).
	census: number;
	quarterDays: number;
	quarterWeekendDays: number;
	// Remaining calendar-day count (anchored to the calendar quarter end). Used to
	// switch PPD math onto a catch-up basis when the period toggle is set to "qtd".
	daysRemaining: number;
	// Remaining weekend days — a weekend item's catch-up window, so a missing catch-up rate can be
	// told apart: no weekend left vs. a cut past its limit.
	weekendDaysRemaining: number;
	// Drives which hour rate + action label variant each plan item shows: "quarter"
	// = sustained per-day rate; "qtd" = catch-up rate over remaining days only.
	periodMode: PeriodMode;
	// Forwarded to the card root so the owner can scroll a freshly-added action into view.
	// Lives on the Paper (not a wrapper) so this card can be a direct grid cell that simply
	// disappears — leaving no empty cell — when the plan is empty and the component returns null.
	scrollRef?: Ref<HTMLDivElement>;
};

/**
 * The standalone "Required actions" card — the bottom-LEFT cell of the 2×2 prediction grid
 * (under the Why breakdown, beside the Plan Summary). Since the redesign moved the
 * Now → Projected → Target verdict + cost into the hero, this component no longer renders the
 * plan-summary card — it's just the itemized list of concrete staffing moves (per item: the
 * "schedule N more hours" line, its marginal points from the cumulative pass, its projected
 * $ cost, and a remove control — see `ActionItemRow`). Returns null for an empty plan, so its grid
 * cell simply doesn't render (no empty bottom-left cell).
 */
export const ActionItems: FC<Props> = ({
	selectedPlan,
	categories,
	pointsScale,
	onRemove,
	census,
	quarterDays,
	quarterWeekendDays,
	daysRemaining,
	weekendDaysRemaining,
	periodMode,
	scrollRef,
}) => {
	const { t } = useTranslation();
	const theme = useTheme();
	const reduceMotion = useReducedMotion();
	// Cumulative-scoring pass (FE analog of the BE greedy's `_to_steps`): credit each
	// plan item its marginal point gain over the items added BEFORE it, walking
	// `selectedPlan` in add-order. Single source of truth for every per-item points figure.
	// Hook runs before the empty-plan early return to keep hook order stable.
	const cumulative = useMemo(() => computeCumulativePass(selectedPlan, categories, pointsScale), [selectedPlan, categories, pointsScale]);
	if (selectedPlan.length === 0) return null;

	// Per-item projected dollar cost — `item.pricedSpend × census × applicableQuarterDays` (a cut is
	// priced from the whole hours it commits, so "Saves $X" matches "Cut N hours"). Mode-INVARIANT:
	// the same total HPPD change costs the same total dollars whether spread across the full quarter
	// or compressed into the remaining catch-up window.
	const itemPrice = (item: PlanItem): number => {
		const days = WEEKEND_REC_IDS.has(item.id) ? quarterWeekendDays : quarterDays;
		return item.pricedSpend * census * days;
	};

	// Effective calendar-day window the displayed PPD averages over. Quarter mode uses all
	// quarter days; QTD mode uses only the remaining catch-up window, so the per-PPD rate
	// scales up. Guards `daysRemaining === 0` (completed quarter) by using the quarter count.
	const effDays = periodMode === "qtd" && daysRemaining > 0 ? daysRemaining : quarterDays;
	const itemDisplayPPD = (item: PlanItem): number => {
		if (census <= 0 || effDays <= 0) return 0;
		return itemPrice(item) / (census * effDays);
	};

	return (
		<Paper
			ref={scrollRef}
			elevation={0}
			sx={{ borderRadius: PBJ_RADIUS.CARD, overflow: "hidden", boxShadow: cardShadow(theme), scrollMarginTop: SCROLL_MARGIN_TOP_PX }}
			data-testid="pbj-model-action-plan"
		>
			<Box sx={{ p: 1.5, px: 2, bgcolor: "background.default", borderBottom: "1px solid", borderColor: "divider" }}>
				<PanelHeader
					icon={<PlaylistAddCheckIcon />}
					title={t("pbj_model.action_plan.required_actions")}
					endingContent={
						<Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
							{t("pbj_model.action_plan.item_count", { count: selectedPlan.length })}
						</Typography>
					}
				/>
			</Box>
			<Box>
				<AnimatePresence initial={false}>
					{selectedPlan.map((item, index) => {
						// The pass scores every plan item, so a missing entry is a broken invariant, not "0 points".
						const itemResult = cumulative.byItemId[item.id];
						if (itemResult === undefined) throw new Error(`Cumulative pass missed plan item ${item.id}`);
						return (
							// Rows animate in (fade + small lift, staggered) and out (faster, with a height
							// collapse so the list closes up) via AnimatePresence + `layout`. GPU props only;
							// reduced-motion drops the movement (mount/unmount become instant). The divider
							// lives INSIDE each row's motion wrapper (top border, not first) so it collapses
							// with the row on exit instead of orphaning.
							<motion.div
								key={item.id}
								layout={!reduceMotion}
								initial={reduceMotion ? false : { opacity: 0, y: 6 }}
								animate={{ opacity: 1, y: 0 }}
								// Faster exit than enter (asymmetric timing) + a height collapse so the list closes up.
								exit={reduceMotion ? { opacity: 0 } : { opacity: 0, height: 0, transition: { duration: MOTION.DURATION.OVERLAY_FADE / 1000 } }}
								transition={
									reduceMotion
										? { duration: 0 }
										: {
												duration: MOTION.DURATION.ENTRANCE_MD / 1000,
												ease: [...MOTION.EASE_OUT_POINTS],
												delay: Math.min(index * MOTION.STAGGER.STEP, MOTION.STAGGER.MAX) / 1000,
											}
								}
								style={{ overflow: "hidden" }}
							>
								{index > 0 && <Divider />}
								<ActionItemRow
									item={item}
									itemResult={itemResult}
									periodMode={periodMode}
									price={itemPrice(item)}
									displayPpd={itemDisplayPPD(item)}
									ppdWindowDays={effDays}
									quarterWeekendDays={quarterWeekendDays}
									applicableDaysRemaining={WEEKEND_REC_IDS.has(item.id) ? weekendDaysRemaining : daysRemaining}
									onRemove={onRemove}
								/>
							</motion.div>
						);
					})}
				</AnimatePresence>
			</Box>
		</Paper>
	);
};
